import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Modal, Stack, Typography } from '@mui/material'
import FormDatePicker from 'components/form-elements/FormDatePicker'
import moment from 'moment'
import React from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { Medicine } from 'types'
import { any, object, string, TypeOf } from 'zod'
import {
  defaultModalStyle,
  defaultModalZIndex,
  FIELD_IS_REQUIRED,
  minButtonWidth,
} from '../../const'
import FormInput from '../form-elements/FormInput'

const formSchema = object({
  medicine: string().min(1, FIELD_IS_REQUIRED),
  form: string().optional(),
  dose: string().optional(),
  takenOn: string().optional(),
  dates: any().optional(),
  directions: string().optional(),
  reason: string().optional(),
  notes: string().optional(),
})

type FormType = TypeOf<typeof formSchema>

interface Props {
  isOpen: boolean
  loading: boolean
  medicine: Medicine | null
  handleClose: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (value: any) => void
}

const FormContent = (props: Props & { medicine: Medicine }) => {
  const { handleClose, loading, onSubmit, medicine } = props

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      medicine: medicine.medicine ?? '',
      form: medicine.form ?? '',
      dose: medicine.dose ?? '',
      takenOn: medicine.takenOn ?? '',
      dates: medicine.dates ? moment(medicine.dates).toDate() : null,
      directions: medicine.directions ?? '',
      reason: medicine.reason ?? '',
      notes: medicine.notes ?? '',
    },
  })

  const { handleSubmit } = methods

  const onSubmitHandler: SubmitHandler<FormType> = (values) => {
    onSubmit(values)
  }

  return (
    <Box sx={defaultModalStyle}>
      <Typography variant="h5" mb={2}>
        Edit Medicine
      </Typography>
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} width="100%" noValidate>
          <Stack spacing={1.5}>
            <FormInput label="Medicine" required fullWidth name="medicine" />
            <FormInput label="Form" fullWidth name="form" />
            <FormInput label="Dose" fullWidth name="dose" />
            <FormInput label="When Taken" fullWidth name="takenOn" />
            <FormDatePicker label="Last updated" fullWidth name="dates" />
            <FormInput label="Directions" fullWidth name="directions" />
            <FormInput label="Reason" fullWidth name="reason" />
            <FormInput label="Notes" fullWidth name="notes" />
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
            <Button disabled={loading} variant="outlined" onClick={handleClose} sx={minButtonWidth}>
              Close
            </Button>
            <LoadingButton loading={loading} variant="contained" type="submit" sx={minButtonWidth}>
              Save
            </LoadingButton>
          </Stack>
        </Box>
      </FormProvider>
    </Box>
  )
}

const EditMedicineModal = (props: Props) => {
  const { isOpen, medicine } = props

  return (
    <Modal sx={defaultModalZIndex} open={isOpen}>
      {isOpen && medicine ? <FormContent {...props} medicine={medicine} /> : <Box />}
    </Modal>
  )
}

export default EditMedicineModal
